import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';

interface ContentCard {
  title: string;
  body: string;
}

interface ContentSection extends ContentCard {
  points?: string[];
}

export default function AboutPage() {
  const { t } = useTranslation('marketing');
  const highlights = t('pages.about.highlights', { returnObjects: true }) as ContentCard[];
  const sections = t('pages.about.sections', { returnObjects: true }) as ContentSection[];
  const sidebarPoints = t('pages.about.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.about.cta.actions', { returnObjects: true }) as Array<{
    href: string;
    label: string;
    variant: 'primary' | 'secondary' | 'ghost';
  }>;

  return (
    <PublicContentPage
      metaTitle={t('pages.about.meta.title')}
      metaDescription={t('pages.about.meta.description')}
      canonicalPath="/about"
      eyebrow={t('pages.about.hero.eyebrow')}
      title={t('pages.about.hero.title')}
      intro={t('pages.about.hero.intro')}
      schemaType="AboutPage"
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.about.sidebar.title')}</h2>
            <p>{t('pages.about.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.about.sidebar.ctaTitle')}</h2>
            <p>{t('pages.about.sidebar.ctaBody')}</p>
            <div className="acc-public-page-actions">
              {ctaActions.map((action) => (
                <PublicCtaLink key={`sidebar-${action.href}`} href={action.href} variant={action.variant}>
                  {action.label}
                </PublicCtaLink>
              ))}
            </div>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.about.description.title')}</h2>
        <p>{t('pages.about.description.body')}</p>
      </section>

      <div className="acc-public-page-grid">
        {highlights.map((highlight) => (
          <article key={highlight.title} className="acc-public-page-subcard">
            <h2>{highlight.title}</h2>
            <p>{highlight.body}</p>
          </article>
        ))}
      </div>

      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-cta-band" aria-labelledby="about-cta-title">
        <div>
          <h2 id="about-cta-title">{t('pages.about.cta.title')}</h2>
          <p>{t('pages.about.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
